// First-run onboarding / "Getting started" screen.
//
// One dedicated server per ISP customer. After a fresh box is provisioned and
// the admin logs in for the first time they land on empty dashboards with no
// guidance. This page walks them through configuring their instance and shows
// real config-completeness driven entirely from live endpoints — nothing here
// is hard-coded or faked.
//
// The completeness logic lives in the exported useOnboardingSteps() hook so the
// dashboard banner and this page compute the exact same thing from one source.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

// ── Shape of one checklist step ────────────────────────────────────────────
export interface OnboardingStep {
  key: string;
  title: string;
  description: string;   // one-line "why this matters"
  done: boolean;
  loading: boolean;
  detail: string;        // live state, e.g. "Acme Fiber · acme"
  note?: string;         // soft recommendation, shown even when done
  cta: { label: string; to: string };
}

// ── The shared completeness hook ────────────────────────────────────────────
// Every query is a plain GET so react-query dedupes/caches across the page and
// the dashboard banner. Chained plant lookup is gated with `enabled`.
export function useOnboardingSteps(): {
  steps: OnboardingStep[];
  doneCount: number;
  total: number;
  loading: boolean;
} {
  const cfgQ = useQuery<any>({
    queryKey: ['deployment-config'],
    queryFn: () => api.get('/bead/config'),
  });
  const coverageQ = useQuery<any>({
    queryKey: ['geodata-coverage'],
    queryFn: () => api.get('/geodata/coverage'),
  });
  const awardsQ = useQuery<any[]>({
    queryKey: ['bead-awards'],
    queryFn: () => api.get('/bead/awards'),
  });
  const catalogsQ = useQuery<any[]>({
    queryKey: ['cost-catalogs'],
    queryFn: () => api.get('/settings/cost-catalogs'),
  });
  const usersQ = useQuery<any>({
    queryKey: ['users'],
    queryFn: () => api.get('/users'),
  });
  const projectsQ = useQuery<any[]>({
    queryKey: ['bead-projects-dashboard'],
    queryFn: () => api.get('/bead/projects'),
  });
  const firstProjectId: string | undefined = Array.isArray(projectsQ.data)
    ? projectsQ.data[0]?.id
    : undefined;
  const plantsQ = useQuery<any[]>({
    queryKey: ['onboarding-plant', firstProjectId],
    queryFn: () => api.get(`/plants/project/${firstProjectId}`),
    enabled: !!firstProjectId,
  });

  const steps = useMemo<OnboardingStep[]>(() => {
    const cfg = cfgQ.data;
    const coverage = coverageQ.data;
    const awards = Array.isArray(awardsQ.data) ? awardsQ.data : [];
    const catalogs = Array.isArray(catalogsQ.data) ? catalogsQ.data : [];
    const users = Array.isArray(usersQ.data?.items) ? usersQ.data.items : [];
    const projects = Array.isArray(projectsQ.data) ? projectsQ.data : [];
    const plants = Array.isArray(plantsQ.data) ? plantsQ.data : [];

    // 1 ── Company identity
    const identityDone = !!(cfg?.isp_name && cfg?.isp_code);
    const idMissing: string[] = [];
    if (!cfg?.officer_name) idMissing.push('signing officer');
    if (!cfg?.contractor_address) idMissing.push('contractor address');
    const identity: OnboardingStep = {
      key: 'identity',
      title: 'Company identity',
      description: 'Name your ISP so it appears on invoices, draws and federal payroll forms.',
      done: identityDone,
      loading: cfgQ.isLoading,
      detail: identityDone
        ? `${cfg.isp_name} · ${cfg.isp_code}`
        : 'ISP name and code not set',
      note: identityDone && idMissing.length
        ? `Recommended for WH-347: add ${idMissing.join(' and ')}.`
        : undefined,
      cta: { label: identityDone ? 'Review' : 'Set company identity', to: '/settings/deployment' },
    };

    // 2 ── States of operation
    const states: string[] = Array.isArray(cfg?.states_of_operation) ? cfg.states_of_operation : [];
    const statesDone = states.length > 0;
    const statesStep: OnboardingStep = {
      key: 'states',
      title: 'States of operation',
      description: 'Pick the states you build in — this scopes geodata, rates and compliance rules.',
      done: statesDone,
      loading: cfgQ.isLoading,
      detail: statesDone ? states.join(', ') : 'No states selected',
      cta: { label: statesDone ? 'Review' : 'Choose states', to: '/settings/deployment' },
    };

    // 3 ── Geodata coverage (buildings + roads are design-critical)
    const covStates: any[] = Array.isArray(coverage?.states) ? coverage.states : [];
    const layers: string[] = Array.isArray(coverage?.layers) ? coverage.layers : [];
    let loadedCells = 0;
    let totalCells = 0;
    let allCriticalLoaded = states.length > 0;
    for (const st of states) {
      const entry = covStates.find((s: any) => s.state === st);
      const cells = entry?.cells ?? {};
      for (const layer of layers) {
        totalCells += 1;
        if (cells?.[layer]?.status === 'done') loadedCells += 1;
      }
      const buildingsOk = cells?.buildings?.status === 'done';
      const roadsOk = cells?.roads?.status === 'done';
      if (!buildingsOk || !roadsOk) allCriticalLoaded = false;
    }
    const geoDone = states.length > 0 && allCriticalLoaded;
    const geoStep: OnboardingStep = {
      key: 'geodata',
      title: 'Geodata coverage',
      description: 'Load buildings and roads for every operating state so designs can run.',
      done: geoDone,
      loading: coverageQ.isLoading || cfgQ.isLoading,
      detail: states.length === 0
        ? 'Select states first'
        : `${loadedCells}/${totalCells} layers loaded across ${states.length} state${states.length === 1 ? '' : 's'}${geoDone ? '' : ' · buildings + roads incomplete'}`,
      cta: { label: geoDone ? 'Review coverage' : 'Load geodata', to: '/settings/deployment' },
    };

    // 4 ── BEAD award
    const fundedAwards = awards.filter(
      (a: any) => a.award_amount_usd != null && Number(a.award_amount_usd) > 0,
    );
    const awardDone = fundedAwards.length > 0;
    const fed = fundedAwards.reduce((s: number, a: any) => s + Number(a.federal_share_usd ?? 0), 0);
    // Federal share is optional on an award. Reporting "$0 federal" for an award that
    // simply has no split recorded reads as a data error, so fall back to the total.
    const awardTotal = fundedAwards.reduce((s: number, a: any) => s + Number(a.award_amount_usd ?? 0), 0);
    const awardStep: OnboardingStep = {
      key: 'award',
      title: 'BEAD award',
      description: 'Record your grant award (or import it from Ready) to unlock budgets and draws.',
      done: awardDone,
      loading: awardsQ.isLoading,
      detail: awardDone
        ? `${fundedAwards.length} award${fundedAwards.length === 1 ? '' : 's'} · ${fed > 0 ? `${fmtUSD(fed)} federal` : `${fmtUSD(awardTotal)} awarded`}`
        : 'No funded award on record',
      cta: { label: awardDone ? 'Review awards' : 'Add / import award', to: '/compliance/awards' },
    };

    // 5 ── Pricing / rate card
    const pricingDone = catalogs.length > 0;  // the seeded default rate card is fully functional
    const defaultCat = catalogs.find((c: any) => c.is_default) ?? catalogs[0];
    const pricingStep: OnboardingStep = {
      key: 'pricing',
      title: 'Pricing / rate card',
      description: 'Confirm your unit costs for cable, labor and permits — budgets build from these.',
      done: pricingDone,
      loading: catalogsQ.isLoading,
      detail: pricingDone
        ? `${catalogs.length} rate card${catalogs.length === 1 ? '' : 's'} · "${defaultCat?.name ?? 'Default'}"`
        : 'No rate card found',
      note: pricingDone && defaultCat?.is_default
        ? 'Still on the seeded default — review the numbers against your own quotes.'
        : undefined,
      cta: { label: pricingDone ? 'Review rate card' : 'Set up pricing', to: '/settings/pricing' },
    };

    // 6 ── Network plant defaults
    const plantDone = plants.length > 0;
    const noProjects = projects.length === 0;
    const firstPlant = plants[0];
    const plantStep: OnboardingStep = {
      key: 'plant',
      title: 'Network plant defaults',
      description: 'Set split ratio and strand defaults for your outside plant so designs size correctly.',
      done: plantDone,
      loading: projectsQ.isLoading || (!!firstProjectId && plantsQ.isLoading),
      detail: noProjects
        ? 'No projects yet — create one to define a plant'
        : plantDone
          ? `${firstPlant?.name ?? 'Plant'} · ${firstPlant?.default_split_ratio ?? '—'} split`
          : 'No plant defined',
      cta: noProjects
        ? { label: 'Create a project', to: '/projects' }
        : { label: plantDone ? 'Review plant settings' : 'Configure plant', to: '/settings' },
    };

    // 7 ── Team
    const activeUsers = users.filter((u: any) => u.is_active);
    const teamDone = activeUsers.length > 1;
    const teamStep: OnboardingStep = {
      key: 'team',
      title: 'Team',
      description: 'Invite your staff so crews, finance and compliance each have their own login.',
      done: teamDone,
      loading: usersQ.isLoading,
      detail: `${activeUsers.length} active user${activeUsers.length === 1 ? '' : 's'}`,
      note: teamDone ? undefined : 'Only the admin account exists — invite your team.',
      cta: { label: teamDone ? 'Manage users' : 'Invite your team', to: '/settings/users' },
    };

    // Condensed: identity + states + geodata all live on the Deployment page, so they
    // are presented as one checklist step with per-part status instead of three rows.
    const setupDone = identityDone && statesDone && geoDone;
    const setupStep: OnboardingStep = {
      key: 'setup',
      title: 'Company & coverage',
      description: 'Name your ISP, choose your states, and load buildings + roads — all on the Deployment page.',
      done: setupDone,
      loading: cfgQ.isLoading || coverageQ.isLoading,
      detail: `Identity ${identityDone ? '✓' : '—'}  ·  States ${statesDone ? '✓' : '—'}  ·  Geodata ${geoDone ? '✓' : '—'}`,
      note: !setupDone && states.length === 0 ? 'Start by naming your ISP and picking your states — geodata scopes to them.' : undefined,
      cta: { label: setupDone ? 'Review setup' : 'Set up instance', to: '/settings/deployment' },
    };
    return [setupStep, awardStep, pricingStep, plantStep, teamStep];
  }, [
    cfgQ.data, cfgQ.isLoading,
    coverageQ.data, coverageQ.isLoading,
    awardsQ.data, awardsQ.isLoading,
    catalogsQ.data, catalogsQ.isLoading,
    usersQ.data, usersQ.isLoading,
    projectsQ.data, projectsQ.isLoading,
    plantsQ.data, plantsQ.isLoading, firstProjectId,
  ]);

  const doneCount = steps.filter((s) => s.done).length;
  const loading = steps.some((s) => s.loading);
  return { steps, doneCount, total: steps.length, loading };
}

function fmtUSD(n: number): string {
  const num = Number(n) || 0;
  if (num >= 1_000_000) return '$' + (num / 1_000_000).toFixed(1) + 'M';
  if (num >= 1_000) return '$' + (num / 1_000).toFixed(0) + 'K';
  return '$' + Math.round(num).toLocaleString();
}

// ── Presentational bits ─────────────────────────────────────────────────────
function StatusDot({ done }: { done: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        flexShrink: 0,
        width: 28,
        height: 28,
        borderRadius: 999,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 15,
        fontWeight: 800,
        background: done ? '#d1fae5' : '#fef3c7',
        color: done ? '#065f46' : '#92400e',
        border: `1px solid ${done ? '#6ee7b7' : '#fcd34d'}`,
      }}
    >
      {done ? '✓' : '•'}
    </span>
  );
}

function StepRow({ step, index }: { step: OnboardingStep; index: number }) {
  const { done } = step;
  return (
    <div
      style={{
        display: 'flex',
        gap: 14,
        alignItems: 'flex-start',
        padding: '16px 18px',
        borderBottom: '1px solid var(--border)',
        background: done ? '#fafffb' : 'white',
      }}
    >
      <StatusDot done={done} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', fontFamily: 'ui-monospace, monospace' }}>
            {String(index + 1).padStart(2, '0')}
          </span>
          <span style={{ fontSize: 15, fontWeight: 700, color: done ? '#475569' : '#0f172a' }}>{step.title}</span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '.05em',
              padding: '2px 8px',
              borderRadius: 999,
              background: done ? '#d1fae5' : '#fef3c7',
              color: done ? '#065f46' : '#92400e',
            }}
          >
            {step.loading ? '…' : done ? 'Done' : 'To do'}
          </span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>{step.description}</div>
        <div style={{ fontSize: 12, color: done ? '#059669' : '#0f172a', marginTop: 6, fontWeight: 600 }}>
          {step.loading ? 'Checking…' : step.detail}
        </div>
        {step.note && (
          <div style={{ fontSize: 12, color: '#b45309', marginTop: 4 }}>{step.note}</div>
        )}
      </div>
      <Link
        to={step.cta.to}
        style={{
          flexShrink: 0,
          alignSelf: 'center',
          textDecoration: 'none',
          fontSize: 13,
          fontWeight: 600,
          padding: '8px 14px',
          borderRadius: 6,
          whiteSpace: 'nowrap',
          background: done ? 'white' : '#0f172a',
          color: done ? '#0f172a' : 'white',
          border: `1px solid ${done ? 'var(--border)' : '#0f172a'}`,
        }}
      >
        {step.cta.label} →
      </Link>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────
export default function GettingStartedPage() {
  const { steps, doneCount, total, loading } = useOnboardingSteps();
  const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;
  const allDone = doneCount === total && !loading;

  return (
    <>
      <div className="page__header">
        <div>
          <h1 className="page__title">Getting started</h1>
          <div className="page__subtitle">Configure your Milestone Build instance — each step reads live from your data.</div>
        </div>
      </div>

      {/* Progress summary */}
      <div className="card" style={{ marginBottom: 16, borderTop: `3px solid ${allDone ? '#10b981' : '#3b82f6'}` }}>
        <div className="card__body" style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
              {loading
                ? 'Checking your instance…'
                : allDone
                  ? 'Your instance is fully configured 🎉'
                  : `Your instance is ${doneCount} of ${total} steps configured`}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: allDone ? '#10b981' : '#0f172a' }}>{pct}%</div>
          </div>
          <div style={{ marginTop: 12, height: 10, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
            <div
              style={{
                width: pct + '%',
                height: '100%',
                background: allDone ? '#10b981' : '#3b82f6',
                transition: 'width 0.4s ease',
              }}
            />
          </div>
          {!allDone && !loading && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10 }}>
              Finish the highlighted steps below to get the most out of your dedicated instance.
            </div>
          )}
        </div>
      </div>

      {/* Checklist */}
      <div className="card">
        <div className="card__header">Setup checklist</div>
        <div className="card__body" style={{ padding: 0 }}>
          {steps.map((s, i) => (
            <StepRow key={s.key} step={s} index={i} />
          ))}
        </div>
      </div>
    </>
  );
}
